/**
 * Quick-amount tiers — curated, psychologically-nice round figures per currency.
 *
 * This replaces the old "USD anchor × rate → snapToNice → exponential spread"
 * approach, which rendered ugly buttons like €4 / €45 / €870 / €8,650. Each
 * currency has its own hand-picked round tiers (the donations-table pattern);
 * currencies without an entry fall back to a {1,2,5}×10ⁿ nice-number snap of the
 * USD anchors scaled by the live rate (never 4 / 45 / 870).
 *
 * The values are LOCAL amounts. Consumers store the selected value as the field
 * value (the field is local), so `toUsdc(value)` round-trips on submit.
 */

import { QUICK_AMOUNTS_BY_CURRENCY, USD_ANCHORS } from '@morfi/onramp-sdk';

export { QUICK_AMOUNTS_BY_CURRENCY, USD_ANCHORS };

/**
 * Snap a positive number to the nearest {1, 2, 5} × 10ⁿ "nice" figure.
 * 4.35 → 5, 43 → 50, 865 → 1000, 8650 → 10000. Whole numbers only for
 * zero-decimal currencies (handled by the caller passing already-scaled values).
 */
export function niceSnap(value: number, decimals: number): number {
  if (value <= 0) return 0;
  const exp = Math.floor(Math.log10(value));
  const pow = 10 ** exp;
  const base = value / pow; // 1..10
  const niceBase = base < 1.5 ? 1 : base < 3.5 ? 2 : base < 7.5 ? 5 : 10;
  const snapped = niceBase * pow;
  return decimals === 0 ? Math.max(1, Math.round(snapped)) : snapped;
}

/** The tier ladder for a currency — its curated table, or a nice-snapped USD ladder. */
export function quickAmountTiers(
  currency: string,
  rate: number,
  decimals: number,
): readonly number[] {
  const curated = QUICK_AMOUNTS_BY_CURRENCY[currency];
  if (curated !== undefined) return curated;
  const safeRate = rate > 0 ? rate : 1;
  const snapped = USD_ANCHORS.map((usd) => niceSnap(usd * safeRate, decimals));
  return [...new Set(snapped)];
}

export interface SelectQuickAmountsOptions {
  /** Available balance in local currency. When > 0, tiers above it are dropped. */
  maxLocal?: number;
  /** Lowest tier to offer (local) — e.g. a funding floor. */
  min?: number;
  /** Max number of buttons. */
  max?: number;
  /** Currency decimals (0 or 2) — keeps the small-balance fallback at the right precision. */
  decimals?: number;
}

/**
 * Pick the buttons to show: filter the ladder to `[min, maxLocal]` and take the
 * lowest `max` (low → mid). No exponential spread across the whole ladder.
 */
export function selectQuickAmounts(
  tiers: readonly number[],
  { maxLocal = 0, min = 0, max = 4, decimals = 2 }: SelectQuickAmountsOptions = {},
): number[] {
  const usable = tiers.filter((v) => v >= min && (maxLocal <= 0 || v <= maxLocal));
  if (usable.length === 0 && maxLocal > 0) {
    // Balance below the smallest tier — offer the spendable balance floored to
    // the currency's precision (cents for 2-decimal, whole units for 0-decimal,
    // so a $4.80 balance stays $4.80, not $4). Never offer below the `min` floor
    // (e.g. a funding minimum) — that button could only fail validation.
    const factor = decimals === 0 ? 1 : 10 ** decimals;
    const rounded = Math.floor(maxLocal * factor) / factor;
    return rounded > 0 && rounded >= min ? [rounded] : [];
  }
  return usable.slice(0, max);
}

/**
 * Resolve a single per-currency amount (e.g. a faucet grant): the table entry,
 * or a nice-snapped `usdFallback × rate`. Generalises the donations goal table.
 */
export function resolveLocalAmount(
  perCurrency: Readonly<Record<string, number>>,
  usdFallback: number,
  currency: string,
  rate: number,
  decimals: number,
): number {
  const picked = perCurrency[currency];
  if (picked !== undefined) return picked;
  const safeRate = rate > 0 ? rate : 1;
  return niceSnap(usdFallback * safeRate, decimals);
}
